import {
  createContext,
  useContext,
  useCallback,
  useRef,
  useState,
  useEffect,
  memo,
  ReactNode,
  JSX,
} from 'react';
import axios from 'src/utils/axios';
import { getNotificationQueryParams } from 'src/utils/notificationFields';
import useAuth from 'src/hooks/useAuth';
import { useRenterNotificationGate } from 'src/hooks/useRenterNotificationGate';
import { useRenterNotificationGateV2 } from 'src/hooks/useRenterNotificationGateV2';
import useBroadcastChannel from 'src/hooks/useBroadcastChannel';
import { POLLING_INTERVALS, API_ENDPOINTS } from 'src/constants/realTime';
import { useEventTracking } from 'src/hooks/useEventTracking';
import { useNotificationWebSocket } from 'src/hooks/useNotificationWebSocket';
import { notifySupportCaseReady } from 'src/components/ConnectChat/supportChatBridge';
import { Notification } from 'src/types/Notifications';

/** Max notifications kept in memory; must match backend for_user limit so grouped counts (e.g. recert) are accurate. */
const MAX_NOTIFICATIONS_IN_LIST = 1000;

interface NotificationContextType {
  // State
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  wsError: string | null;
  wsConnected: boolean;

  // Actions
  fetchNotifications: (showLoading?: boolean) => Promise<void>;
  markAsRead: (notifId: number) => Promise<void>;
  markAsUnread: (notifId: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  bulkMarkAsRead: (notifIds: number[]) => Promise<void>;
  bulkMarkAsUnread: (notifIds: number[]) => Promise<void>;
}

// Create context
const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

// Hook to use the context
export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider',
    );
  }
  return context;
};

// Provider component
export const NotificationProvider = memo(
  ({ children }: { children: ReactNode }): JSX.Element => {
    const { user } = useAuth();
    const { trackError } = useEventTracking();
    const legacyGate = useRenterNotificationGate();
    const v2Gate = useRenterNotificationGateV2();
    const effectiveGate = user?.sees_new_experience ? v2Gate : legacyGate;
    const renterGateEnabled = effectiveGate.isEnabled;

    // Helper function to set optimistic update timestamp
    const setOptimisticUpdateTimestamp = useCallback((timestamp: number) => {
      lastOptimisticUpdateRef.current = timestamp;
    }, []);

    // User and permissions
    const isResident = Boolean(
      user?.isResident ||
      user?.role === 'resident' ||
      user?.groups?.some((group) => group?.name?.toLowerCase() === 'resident'),
    );
    const canUseNotifications = isResident
      ? renterGateEnabled
      : user?.canUseRealTimeNotificationsFileOwner ||
        user?.canUseRealTimeNotificationsWatched ||
        user?.canUseRealTimeNotificationsProperty;
    const canUseWebSockets = isResident
      ? renterGateEnabled
      : user?.canUseWebSockets;

    // Notification state
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Shared refs
    const notificationUpdatingRef = useRef<boolean>(false);
    const lastOptimisticUpdateRef = useRef<number>(0);
    const initialFetchDoneRef = useRef<boolean>(false);

    // Cross-tab communication
    const {
      message: broadcastMessage,
      isReady: broadcastReady,
      sendMessage: sendBroadcast,
    } = useBroadcastChannel('real-time-notifications');

    // WebSocket integration for new notifications
    const handleWebSocketNotification = useCallback(
      (notificationData: Notification) => {
        setNotifications((prevNotifications) => {
          const existingIndex = prevNotifications.findIndex(
            (n) => n.id === notificationData.id,
          );
          if (existingIndex >= 0) {
            // Update existing notification
            const updated = [...prevNotifications];
            updated[existingIndex] = {
              ...updated[existingIndex],
              ...notificationData,
            };
            return updated;
          } else {
            // Add new notification at the beginning
            const newNotifications = [notificationData, ...prevNotifications];
            return newNotifications.slice(0, MAX_NOTIFICATIONS_IN_LIST);
          }
        });

        if (!notificationData.is_read) {
          setUnreadCount((prev) => {
            const total = prev + 1;
            return total < 0 ? 0 : total;
          });
        }

        // ENG-4185 — a scheduled run finished. This context owns the one notification
        // socket, so it is the only place that can turn the frame into "open the panel
        // on that case": asking the launcher through the bridge keeps a single owner
        // of the panel's open state instead of a second socket in the FAB.
        const related = (
          notificationData as unknown as {
            related_objects?: {
              type?: string;
              system_event?: string;
              support_case_id?: number;
            };
          }
        )?.related_objects;
        if (
          related?.type === 'system' &&
          related.system_event === 'support_case_ready' &&
          typeof related.support_case_id === 'number'
        ) {
          notifySupportCaseReady(related.support_case_id);
        }
      },
      [],
    );

    interface ReadStatusData {
      is_read: boolean;
      read_at?: string;
    }

    const handleWebSocketNotificationReadStatus = useCallback(
      (
        notificationData:
          | Notification
          | { read_all?: boolean; [key: string]: boolean | ReadStatusData },
      ): void => {
        if (notificationData.read_all) {
          setNotifications((prevNotifications) => {
            return prevNotifications.map((notif) => ({
              ...notif,
              is_read: true,
              read_at: new Date().toISOString(),
            }));
          });
          setUnreadCount(0);
          return;
        }

        setNotifications((prevNotifications) => {
          const updated = prevNotifications.map((notif) => {
            const statusData = notificationData[`${notif.id}`];
            if (statusData) {
              // Handle both old format (boolean) and new format (object with is_read and read_at)
              if (typeof statusData === 'boolean') {
                return {
                  ...notif,
                  is_read: statusData,
                  read_at: statusData ? new Date().toISOString() : undefined,
                };
              } else if (
                typeof statusData === 'object' &&
                statusData !== null &&
                'is_read' in statusData
              ) {
                return {
                  ...notif,
                  is_read: statusData.is_read,
                  read_at:
                    statusData.read_at ||
                    (statusData.is_read ? new Date().toISOString() : undefined),
                };
              }
            }
            return notif;
          });
          return updated;
        });

        const sum = Object.values(notificationData).reduce(
          (acc: number, statusData: boolean | ReadStatusData | undefined) => {
            if (
              statusData === true ||
              (typeof statusData === 'object' && statusData?.is_read === true)
            ) {
              return acc - 1;
            } else if (
              statusData === false ||
              (typeof statusData === 'object' && statusData?.is_read === false)
            ) {
              return acc + 1;
            }
            return acc;
          },
          0,
        );
        setUnreadCount((prev) => {
          const total = prev + sum;
          return total < 0 ? 0 : total;
        });
      },
      [],
    );

    const { isConnected: wsConnected, connectionError: wsError } =
      useNotificationWebSocket({
        onNotification: handleWebSocketNotification,
        onNotificationReadStatus: handleWebSocketNotificationReadStatus,
        enabled: canUseNotifications && canUseWebSockets,
      });

    const fetchNotifications = useCallback(
      async (showLoading = true): Promise<void> => {
        if (!canUseNotifications) {
          return;
        }

        if (notificationUpdatingRef.current) {
          return;
        }
        notificationUpdatingRef.current = true;

        const startTime = Date.now();
        const isPolling = !showLoading;

        if (showLoading) {
          setLoading(true);
        }
        setError(null);

        try {
          const notificationParams = getNotificationQueryParams();
          const renterSourceParam =
            isResident && renterGateEnabled
              ? '&notification_source=renter'
              : '';
          const { data } = await axios.get(
            `/email_notifications/email_notifications/for_user/?${notificationParams}${renterSourceParam}`,
          );
          const newNotifications = data.results || [];

          const timeSinceLastUpdate =
            Date.now() - lastOptimisticUpdateRef.current;

          setNotifications((prevNotifications) => {
            // If we've had a recent optimistic update (within 5 seconds), merge carefully
            if (timeSinceLastUpdate < 5000) {
              // Create a map of current optimistic updates
              const optimisticUpdates = new Map();
              prevNotifications.forEach((notif) => {
                optimisticUpdates.set(notif.id, { is_read: notif.is_read });
              });

              // Merge server data with optimistic updates
              const mergedNotifications = newNotifications.map(
                (serverNotif) => {
                  const optimisticUpdate = optimisticUpdates.get(
                    serverNotif.id,
                  );
                  if (optimisticUpdate) {
                    // Preserve the optimistic update for is_read status
                    return {
                      ...serverNotif,
                      is_read: optimisticUpdate.is_read,
                    };
                  }
                  return serverNotif;
                },
              );

              // Calculate unread count from merged notifications
              const mergedUnreadCount = mergedNotifications.filter(
                (n) => !n.is_read,
              ).length;
              setUnreadCount(mergedUnreadCount);

              return mergedNotifications;
            }

            // No recent optimistic updates, use server data as-is
            setUnreadCount(data.unread_count || 0);
            return newNotifications;
          });

          // Broadcast updates to other tabs
          if (isPolling && !canUseWebSockets && broadcastReady) {
            try {
              sendBroadcast({
                type: 'notifications_updated',
                data: {
                  notifications: newNotifications,
                  unreadCount: data.unread_count || 0,
                },
              });
            } catch {
              // Don't fail the entire request if broadcasting fails
            }
          }
        } catch (error) {
          const responseTime = Date.now() - startTime;

          // Track notification fetch error
          trackError(error, {
            context: 'notification_fetch',
            action: isPolling ? 'polling_error' : 'initial_fetch_error',
            response_time: responseTime,
            polling_interval: POLLING_INTERVALS.REAL_TIME_NOTIFICATIONS,
          });

          setError('Failed to load notifications.');
        } finally {
          if (showLoading) {
            setLoading(false);
          }
          notificationUpdatingRef.current = false;
        }
      },
      [
        canUseNotifications,
        trackError,
        canUseWebSockets,
        broadcastReady,
        sendBroadcast,
        isResident,
        renterGateEnabled,
      ],
    );

    // Initial fetch when user is available
    useEffect(() => {
      if (user && canUseNotifications && !initialFetchDoneRef.current) {
        initialFetchDoneRef.current = true;
        fetchNotifications(true);
      }
    }, [user, canUseNotifications, fetchNotifications]);

    // Notification actions
    const markAsRead = useCallback(
      async (notifId: number): Promise<void> => {
        if (!canUseNotifications) {
          return;
        }

        const startTime = Date.now();

        // Optimistic update, only if not using web sockets
        if (!canUseWebSockets) {
          setNotifications((prev) =>
            prev.map((notif) =>
              notif.id === notifId ? { ...notif, is_read: true } : notif,
            ),
          );

          setUnreadCount((prev) => Math.max(0, prev - 1));
          // Record the time of this optimistic update
          setOptimisticUpdateTimestamp(Date.now());
        }

        try {
          await axios.post(
            `/${API_ENDPOINTS.EMAIL_NOTIFICATIONS}${notifId}/mark_read/`,
          );
        } catch (error) {
          const responseTime = Date.now() - startTime;

          // Track mark as read error
          trackError(error, {
            context: 'notification_mark_as_read',
            action: 'mark_as_read_error',
            notification_id: notifId,
            response_time: responseTime,
          });

          // Revert optimistic update on error
          setNotifications((prev) =>
            prev.map((notif) =>
              notif.id === notifId ? { ...notif, is_read: false } : notif,
            ),
          );
          setUnreadCount((prev) => prev + 1);
        }
      },
      [
        canUseNotifications,
        trackError,
        setOptimisticUpdateTimestamp,
        canUseWebSockets,
      ],
    );

    const markAsUnread = useCallback(
      async (notifId: number): Promise<void> => {
        if (!canUseNotifications) {
          return;
        }

        const startTime = Date.now();

        // Optimistic update
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.id === notifId ? { ...notif, is_read: false } : notif,
          ),
        );
        setUnreadCount((prev) => prev + 1);

        // Record the time of this optimistic update
        setOptimisticUpdateTimestamp(Date.now());

        try {
          await axios.post(
            `/${API_ENDPOINTS.EMAIL_NOTIFICATIONS}${notifId}/mark_unread/`,
          );
        } catch (error) {
          const responseTime = Date.now() - startTime;

          // Track mark as unread error
          trackError(error, {
            context: 'notification_mark_as_unread',
            action: 'mark_as_unread_error',
            notification_id: notifId,
            response_time: responseTime,
          });

          // Revert optimistic update on error
          setNotifications((prev) =>
            prev.map((notif) =>
              notif.id === notifId ? { ...notif, is_read: true } : notif,
            ),
          );
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
      },
      [canUseNotifications, trackError, setOptimisticUpdateTimestamp],
    );

    const markAllAsRead = useCallback(async (): Promise<void> => {
      if (!canUseNotifications) {
        return;
      }

      // Optimistic update
      setNotifications((prev) =>
        prev.map((notif) => ({ ...notif, is_read: true })),
      );
      setUnreadCount(0);

      // Record the time of this optimistic update
      setOptimisticUpdateTimestamp(Date.now());

      try {
        await axios.post(
          '/email_notifications/email_notifications/mark_all_read/',
        );
      } catch {
        // Revert optimistic update on error
        setNotifications((prev) =>
          prev.map((notif) => ({ ...notif, is_read: false })),
        );
        setUnreadCount(0);
      }
    }, [canUseNotifications, setOptimisticUpdateTimestamp]);

    const bulkMarkAsRead = useCallback(
      async (notifIds: number[]): Promise<void> => {
        if (!canUseNotifications) {
          return;
        }

        // Optimistic update: only subtract count of notifications that were actually unread
        // (e.g. recert group can have mix of read/unread or be fully read)
        // Use a local variable so concurrent bulkMarkAsRead calls each revert the correct count on error
        let subtractedForThisCall = 0;
        setNotifications((prev) => {
          const unreadToSubtract = prev.filter(
            (n) => notifIds.includes(n.id) && !n.is_read,
          ).length;
          subtractedForThisCall = unreadToSubtract;
          setUnreadCount((c) => Math.max(0, c - unreadToSubtract));
          return prev.map((notif) =>
            notifIds.includes(notif.id) ? { ...notif, is_read: true } : notif,
          );
        });

        // Record the time of this optimistic update
        setOptimisticUpdateTimestamp(Date.now());

        try {
          await axios.post(
            '/email_notifications/email_notifications/mark_list_read/',
            {
              ids: notifIds,
            },
          );
        } catch {
          // Revert optimistic update on error (use captured count for this call only)
          setNotifications((prev) =>
            prev.map((notif) =>
              notifIds.includes(notif.id)
                ? { ...notif, is_read: false }
                : notif,
            ),
          );
          setUnreadCount((prev) => prev + subtractedForThisCall);
        }
      },
      [canUseNotifications, setOptimisticUpdateTimestamp],
    );

    const bulkMarkAsUnread = useCallback(
      async (notifIds: number[]): Promise<void> => {
        if (!canUseNotifications) {
          return;
        }

        // Optimistic update
        setNotifications((prev) =>
          prev.map((notif) =>
            notifIds.includes(notif.id) ? { ...notif, is_read: false } : notif,
          ),
        );
        setUnreadCount((prev) => prev + notifIds.length);

        // Record the time of this optimistic update
        setOptimisticUpdateTimestamp(Date.now());

        try {
          // Since there's no bulk unread endpoint, call individual mark_unread endpoints
          await Promise.all(
            notifIds.map((notifId) =>
              axios.post(
                `/${API_ENDPOINTS.EMAIL_NOTIFICATIONS}${notifId}/mark_unread/`,
              ),
            ),
          );
        } catch {
          // Revert optimistic update on error
          setNotifications((prev) =>
            prev.map((notif) =>
              notifIds.includes(notif.id) ? { ...notif, is_read: true } : notif,
            ),
          );
          setUnreadCount((prev) => Math.max(0, prev - notifIds.length));
        }
      },
      [canUseNotifications, setOptimisticUpdateTimestamp],
    );

    // Handle cross-tab messages
    useEffect(() => {
      if (!broadcastMessage) {
        return;
      }

      const { type, data } = broadcastMessage as {
        type: string;
        data: { notifications: Notification[]; unreadCount: number };
      };

      if (type === 'notifications_updated') {
        setNotifications(data.notifications);
        setUnreadCount(data.unreadCount);
      }
    }, [broadcastMessage]);

    const value: NotificationContextType = {
      // State
      notifications,
      unreadCount,
      loading,
      error: error,
      wsError: wsError,
      wsConnected,

      // Actions
      fetchNotifications,
      markAsRead,
      markAsUnread,
      markAllAsRead,
      bulkMarkAsRead,
      bulkMarkAsUnread,
    };

    return (
      <NotificationContext.Provider value={value}>
        {children}
      </NotificationContext.Provider>
    );
  },
);

NotificationProvider.displayName = 'NotificationProvider';
